import React from 'react';
import Switch from '@mui/material/Switch';
import Icon from '@mui/material/Icon';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Alert from '@mui/material/Alert';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import { alpha, useTheme as useMuiTheme } from '@mui/material/styles';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';

import { smsApi } from '../../api/smsApi';
import { SectionCard } from 'sectors/pilates/settings/ui/SettingsUi';
import { NotificationsRow } from 'sectors/pilates/settings/tabs/notifications/NotificationsRow';

import { BusinessRulesModal } from '../modals/BusinessRulesModal';
import { pilatesSettingsApi } from 'sectors/pilates/settings/pilatesSettingsApi';
import { SmsTestModal } from '../modals/SmsTestModal';

/* ---------------- styles ---------------- */
function pillButtonSx(theme, { disabled }) {
  const isDark = theme.palette.mode === 'dark';
  const border = isDark ? alpha('#fff', 0.16) : alpha('#0b1220', 0.14);
  const bg = isDark ? alpha('#fff', 0.06) : alpha('#0b1220', 0.04);
  const hoverBg = isDark ? alpha('#fff', 0.09) : alpha('#0b1220', 0.06);

  return {
    height: 32,
    minHeight: 32,
    borderRadius: 999,
    px: 1.15,
    fontWeight: 900,
    textTransform: 'none',
    border: `1px solid ${border}`,
    background: bg,
    boxShadow: isDark ? '0 8px 18px rgba(0,0,0,0.22)' : '0 8px 18px rgba(15,23,42,0.08)',
    transition: 'all 140ms ease',
    ...(disabled
      ? { opacity: 0.6 }
      : {
          '&:hover': {
            background: hoverBg,
            transform: 'translateY(-1px)',
            boxShadow: isDark ? '0 10px 22px rgba(0,0,0,0.28)' : '0 10px 22px rgba(15,23,42,0.12)',
          },
          '&:active': { transform: 'translateY(0)' },
        }),
  };
}

/* ---------------- helpers ---------------- */
function buildDraftFromSms(input) {
  const w = input?.rawSettings || input?.raw || input || {};
  return {
    enabled: !!w.enabled,

    attendanceReminder: {
      enabled: !!w.attendanceReminder?.enabled,
      hoursBefore: Number(w.attendanceReminder?.hoursBefore ?? 24),
      templateKey: w.attendanceReminder?.templateKey || 'ATTENDANCE_REMINDER_24H',
    },

    attendanceInquiryPage: {
      showRemainingRights: !!w.attendanceInquiryPage?.showRemainingRights,
      showBusinessRules: !!w.attendanceInquiryPage?.showBusinessRules,
      allowPaymentReminder: !!w.attendanceInquiryPage?.allowPaymentReminder,
    },

    paidSessionInfo: {
      enabled: !!w.paidSessionInfo?.enabled,
      templateKey: w.paidSessionInfo?.templateKey || 'PAID_SESSION_CREATED',
    },

    birthday: {
      enabled: !!w.birthday?.enabled,
      sendAtHourLocal: Number(w.birthday?.sendAtHourLocal ?? 10),
      templateKey: w.birthday?.templateKey || 'BIRTHDAY',
    },
    paymentAutoReminder: {
      enabled: !!w.paymentAutoReminder?.enabled,
      daysAfterDue: Number(w.paymentAutoReminder?.daysAfterDue ?? 0), // örn: vadesi geçince kaç gün sonra
      templateKey: w.paymentAutoReminder?.templateKey || 'PAYMENT_REMINDER',
    },
  };
}

function setDeep(obj, path, value) {
  const next = structuredClone(obj);
  const parts = path.split('.');
  let cur = next;
  for (let i = 0; i < parts.length - 1; i += 1) {
    const k = parts[i];
    cur[k] = cur[k] ?? {};
    cur = cur[k];
  }
  cur[parts[parts.length - 1]] = value;
  return next;
}

export function SmsTabSettings({ sms, refreshSettings }) {
  const theme = useMuiTheme();
  const isDark = theme.palette.mode === 'dark';

  const locked = !!(sms?.gate?.closed || sms?.gate?.noQuota);
  const shellBorder = alpha(theme.palette.text.primary, isDark ? 0.14 : 0.1);
  const softColBg = isDark ? alpha('#0b1220', 0.2) : alpha('#fff', 0.6);

  const [draft, setDraft] = React.useState(() => buildDraftFromSms(sms));
  const [saving, setSaving] = React.useState(false);
  const [saveOk, setSaveOk] = React.useState('');
  const [saveErr, setSaveErr] = React.useState('');

  // ✅ Business rules ayrı state (sms DTO’da yok)
  const [businessRules, setBusinessRules] = React.useState([]);
  const [rulesOpen, setRulesOpen] = React.useState(false);
  const [rulesLoading, setRulesLoading] = React.useState(false);
  const [rulesSaving, setRulesSaving] = React.useState(false);

  // sms değişince draft reset
  React.useEffect(() => {
    setDraft(buildDraftFromSms(sms));
    setSaveOk('');
    setSaveErr('');
  }, [sms]);

  const initial = React.useMemo(() => buildDraftFromSms(sms), [sms]);
  const dirty = React.useMemo(
    () => JSON.stringify(initial) !== JSON.stringify(draft),
    [initial, draft]
  );

  const smsEnabled = !!draft.enabled;
  const disabledAll = locked || !smsEnabled;

  const switchProps = React.useMemo(
    () => ({ size: 'small', disabled: locked || !smsEnabled }),
    [locked, smsEnabled]
  );

  const blockSx = React.useMemo(
    () => ({
      p: 1,
      borderRadius: '14px',
      border: `1px solid ${shellBorder}`,
      bgcolor: isDark ? alpha('#0b1220', 0.16) : alpha('#0b1220', 0.02),
      opacity: smsEnabled ? 1 : 0.6,
      pointerEvents: smsEnabled ? 'auto' : 'none',
      transition: 'opacity .15s ease',
    }),
    [shellBorder, isDark, smsEnabled]
  );

  const wrapperSx = React.useMemo(
    () => ({
      p: 1.1,
      borderRadius: '16px',
      border: `1px solid ${shellBorder}`,
      bgcolor: softColBg,
      opacity: locked ? 0.55 : 1,
      pointerEvents: locked ? 'none' : 'auto',
    }),
    [shellBorder, softColBg, locked]
  );

  const patch = React.useCallback((path, value) => {
    setDraft((prev) => setDeep(prev, path, value));
    setSaveOk('');
    setSaveErr('');
  }, []);

  const onSave = React.useCallback(async () => {
    setSaving(true);
    setSaveOk('');
    setSaveErr('');
    try {
      await smsApi.updateSettings(draft);
      setSaveOk('Kaydedildi.');
      await refreshSettings?.();
    } catch (e) {
      setSaveErr(e?.message || 'Kaydetme başarısız.');
    } finally {
      setSaving(false);
    }
  }, [draft, refreshSettings]);

  const onReset = React.useCallback(() => {
    setDraft(initial);
    setSaveOk('');
    setSaveErr('');
  }, [initial]);

  const att = draft.attendanceReminder;
  const inquiry = draft.attendanceInquiryPage;
  const payAuto = draft.paymentAutoReminder;
  const showConditions = !!att.enabled;
  const [testOpen, setTestOpen] = React.useState(false);
  const [testConfig, setTestConfig] = React.useState(null);

  // hangi test butonuna basıldıysa modal aç
  const openTestModal = React.useCallback((cfg) => {
    setTestConfig(cfg);
    setTestOpen(true);
  }, []);

  const closeTestModal = React.useCallback(() => {
    setTestOpen(false);
  }, []);

  // ✅ business rules load
  React.useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setRulesLoading(true);
        const res = await pilatesSettingsApi.getBusinessRules(); // { rules, updatedAt }
        if (!alive) return;
        setBusinessRules(res?.rules || []);
      } catch (e) {
        // istersen: setSaveErr(e?.message || 'Kurallar alınamadı.');
      } finally {
        if (alive) setRulesLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const saveBusinessRules = React.useCallback(async (rulesArr) => {
    setRulesSaving(true);
    setSaveOk('');
    setSaveErr('');
    try {
      const res = await pilatesSettingsApi.updateBusinessRules(rulesArr);
      setBusinessRules(res?.rules || rulesArr);
      setRulesOpen(false);
      setSaveOk('Kurallar kaydedildi.');
    } catch (e) {
      setSaveErr(e?.message || 'Kurallar kaydedilemedi.');
    } finally {
      setRulesSaving(false);
    }
  }, []);
  const handleTestClick = React.useCallback(
    (type) => {
      const map = {
        ATTENDANCE_REMINDER: {
          title: 'Yoklama Hatırlatması - Test',
          subtitle: 'Seans öncesi hatırlatma mesajı',
          type: 'ATTENDANCE_REMINDER',
        },
        PAYMENT_REMINDER: {
          title: 'Otomatik Ödeme Hatırlatma - Test',
          subtitle: 'Borcu olan üyeye otomatik ödeme hatırlatma mesajı',
          type: 'PAYMENT_REMINDER',
        },
        PAID_SESSION_INFO: {
          title: 'Ücretli Seans - Test',
          subtitle: 'Yeni ücretli seans bilgilendirmesi',
          type: 'PAID_SESSION_INFO',
        },
        BIRTHDAY: {
          title: 'Doğum Günü - Test',
          subtitle: 'Doğum günü mesajı',
          type: 'BIRTHDAY',
        },
      };

      openTestModal(map[type]);
    },
    [openTestModal]
  );

  return (
    <SectionCard
      icon="chat"
      title="sms Otomasyonları"
      subtitle="Hatırlatmalar ve otomatik bilgilendirmeler"
      right={
        dirty ? (
          <MDTypography variant="button" sx={{ fontWeight: 900, opacity: 0.85 }}>
            Değişiklik var
          </MDTypography>
        ) : null
      }
    >
      {locked && (
        <Alert severity={sms?.gate?.closed ? 'warning' : 'error'} sx={{ mb: 1 }}>
          {sms?.gate?.closed
            ? 'sms modülü kapalı. Ayarlar şu an değiştirilemez.'
            : 'Mesaj hakkınız yok. Ayarlar şu an değiştirilemez.'}
        </Alert>
      )}

      {/* ÜST GENEL ANAHTAR */}
      <MDBox sx={wrapperSx}>
        <NotificationsRow
          label="sms bildirimleri aktif"
          hint="Kapalıysa hiçbir sms mesajı gönderilmez"
          right={
            <Switch checked={smsEnabled} onChange={(e) => patch('enabled', e.target.checked)} />
          }
        />
      </MDBox>

      {/* KAYDET / İPTAL */}
      <MDBox mt={1} display="flex" justifyContent="flex-end">
        <Stack direction="row" spacing={1} alignItems="center">
          {saveOk ? (
            <MDTypography variant="button" color="success" sx={{ fontWeight: 900 }}>
              {saveOk}
            </MDTypography>
          ) : null}
          {saveErr ? (
            <MDTypography variant="button" color="error" sx={{ fontWeight: 900 }}>
              {saveErr}
            </MDTypography>
          ) : null}

          <Button
            disabled={!dirty || saving || locked}
            onClick={onReset}
            startIcon={<Icon sx={{ fontSize: 18 }}>undo</Icon>}
            sx={pillButtonSx(theme, { disabled: !dirty || saving || locked })}
          >
            İptal
          </Button>

          <Button
            disabled={!dirty || saving || locked}
            onClick={onSave}
            startIcon={<Icon sx={{ fontSize: 18 }}>{saving ? 'hourglass_top' : 'save'}</Icon>}
            sx={pillButtonSx(theme, { disabled: !dirty || saving || locked })}
          >
            Kaydet
          </Button>
        </Stack>
      </MDBox>

      <MDBox mt={1} sx={{ display: 'grid', gap: 1 }}>
        {/* Yoklama */}
        <MDBox sx={blockSx}>
          <MDBox display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
            <MDTypography
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                fontWeight: 900,
                fontSize: 13,
              }}
            >
              <Icon sx={{ fontSize: 18, opacity: 0.85 }}>event</Icon>
              Yoklama Hatırlatması
            </MDTypography>

            {smsEnabled && !!att.enabled ? (
              <Button
                size="small"
                onClick={() => handleTestClick('ATTENDANCE_REMINDER')}
                startIcon={<Icon sx={{ fontSize: 18 }}>send</Icon>}
                sx={pillButtonSx(theme, { disabled: false })}
              >
                Test Et
              </Button>
            ) : null}
          </MDBox>

          <NotificationsRow
            label="Seans öncesi hatırlatma"
            hint={`Seanslardan 1 gün önce saat 12:00 da otomatik katılım mesajı`}
            right={
              <Switch
                {...switchProps}
                checked={!!att.enabled}
                onChange={(e) => patch('attendanceReminder.enabled', e.target.checked)}
              />
            }
          />
          {/* Şartlar (hatırlatma açıkken) */}
          {showConditions ? (
            <MDBox
              sx={{
                mt: 0,
                p: 1,
                borderRadius: '12px',
                border: `1px dashed ${shellBorder}`,
                bgcolor: isDark ? alpha('#0b1220', 0.22) : alpha('#0b1220', 0.03),
                opacity: smsEnabled ? 1 : 0.6,
                pointerEvents: disabledAll ? 'none' : 'auto',
              }}
            >
              <MDTypography sx={{ fontWeight: 900, fontSize: 12.5, mb: 0.75, opacity: 0.9 }}>
                Hatırlatma İçeriği / Şartlar
              </MDTypography>

              <Stack spacing={0.25}>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={!!inquiry.showRemainingRights}
                      onChange={(e) =>
                        patch('attendanceInquiryPage.showRemainingRights', e.target.checked)
                      }
                    />
                  }
                  label={
                    <MDTypography variant="button" sx={{ fontWeight: 700, opacity: 0.85 }}>
                      Katılım sorgulama ekranında üyenin kalan hakkı gösterilsin
                    </MDTypography>
                  }
                />

                {/* Business rules row + edit */}
                <MDBox display="flex" alignItems="center" justifyContent="space-between" gap={1}>
                  <FormControlLabel
                    sx={{ m: 0 }}
                    control={
                      <Checkbox
                        size="small"
                        checked={!!inquiry.showBusinessRules}
                        onChange={(e) =>
                          patch('attendanceInquiryPage.showBusinessRules', e.target.checked)
                        }
                      />
                    }
                    label={
                      <MDTypography variant="button" sx={{ fontWeight: 700, opacity: 0.85 }}>
                        İşletme kuralları gösterilsin
                      </MDTypography>
                    }
                  />

                  {inquiry.showBusinessRules ? (
                    <Button
                      size="small"
                      onClick={() => setRulesOpen(true)}
                      startIcon={<Icon sx={{ fontSize: 18 }}>edit</Icon>}
                      sx={pillButtonSx(theme, { disabled: false })}
                    >
                      Düzenle
                    </Button>
                  ) : null}
                </MDBox>

                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={!!inquiry.allowPaymentReminder}
                      onChange={(e) =>
                        patch('attendanceInquiryPage.allowPaymentReminder', e.target.checked)
                      }
                    />
                  }
                  label={
                    <MDTypography variant="button" sx={{ fontWeight: 700, opacity: 0.85 }}>
                      Katılım sorgulama ekranında ödeme hatırlatması yapılabilsin
                    </MDTypography>
                  }
                />

                <MDTypography sx={{ fontSize: 11.5, opacity: 0.65, mt: 0.5 }}>
                  Bu seçenekler, üye mesajı üzerinden açılan “katılım sorgulama” ekranının içeriğini
                  etkiler.
                </MDTypography>
              </Stack>
            </MDBox>
          ) : null}
        </MDBox>

        {/* Otomatik ödeme hatırlatma */}
        <MDBox sx={blockSx}>
          <MDBox display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
            <MDTypography
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                fontWeight: 900,
                fontSize: 13,
              }}
            >
              <Icon sx={{ fontSize: 18, opacity: 0.85 }}>payments</Icon>
              Otomatik Ödeme Hatırlatma
            </MDTypography>

            {smsEnabled && !!payAuto?.enabled ? (
              <Button
                size="small"
                onClick={() => handleTestClick('PAYMENT_REMINDER')}
                startIcon={<Icon sx={{ fontSize: 18 }}>send</Icon>}
                sx={pillButtonSx(theme, { disabled: false })}
              >
                Test Et
              </Button>
            ) : null}
          </MDBox>

          <NotificationsRow
            label="Borcu olan üyeye otomatik hatırlatma"
            hint="Yakında • Borcu olan üyeye otomatik ödeme hatırlatma"
            right={
              <Switch
                {...switchProps}
                disabled
                checked={!!payAuto?.enabled}
                onChange={(e) => patch('paymentAutoReminder.enabled', e.target.checked)}
              />
            }
          />
        </MDBox>

        {/* Ücretli seans */}
        <MDBox sx={blockSx}>
          <MDBox display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
            <MDTypography
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                fontWeight: 900,
                fontSize: 13,
              }}
            >
              <Icon sx={{ fontSize: 18, opacity: 0.85 }}>paid</Icon>
              Ücretli veya Randevu seansları için bilgilendirme
            </MDTypography>

            {smsEnabled && !!draft.paidSessionInfo.enabled ? (
              <Button
                size="small"
                onClick={() => handleTestClick('PAID_SESSION_INFO')}
                startIcon={<Icon sx={{ fontSize: 18 }}>send</Icon>}
                sx={pillButtonSx(theme, { disabled: false })}
              >
                Test Et
              </Button>
            ) : null}
          </MDBox>

          <NotificationsRow
            label="Yeni ücretli veya Randevu seansı oluşturulunca"
            hint="Üyeye otomatik bilgilendirme mesajı"
            right={
              <Switch
                {...switchProps}
                checked={!!draft.paidSessionInfo.enabled}
                onChange={(e) => patch('paidSessionInfo.enabled', e.target.checked)}
              />
            }
          />
        </MDBox>

        {/* Doğum günü */}
        <MDBox sx={blockSx}>
          <MDBox display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
            <MDTypography
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                fontWeight: 900,
                fontSize: 13,
              }}
            >
              <Icon sx={{ fontSize: 18, opacity: 0.85 }}>cake</Icon>
              Doğum Günü
            </MDTypography>

            {smsEnabled && !!draft.birthday.enabled ? (
              <Button
                size="small"
                onClick={() => handleTestClick('BIRTHDAY')}
                startIcon={<Icon sx={{ fontSize: 18 }}>send</Icon>}
                sx={pillButtonSx(theme, { disabled: false })}
              >
                Test Et
              </Button>
            ) : null}
          </MDBox>

          <NotificationsRow
            label="Doğum günü mesajı gönder"
            hint={`saat ${draft.birthday.sendAtHourLocal}:00 da doğum günü olan üyeye gönderilir`}
            right={
              <Switch
                {...switchProps}
                checked={!!draft.birthday.enabled}
                onChange={(e) => patch('birthday.enabled', e.target.checked)}
              />
            }
          />
        </MDBox>
      </MDBox>

      {/* footer info */}
      <MDBox mt={1.25}>
        <Alert severity="info" sx={{ borderRadius: '14px' }}>
          Ödeme mesajları ve özel mesajlar, <b>Üyeler</b> ekranından manuel olarak gönderilir.
        </Alert>
      </MDBox>

      <Divider sx={{ mt: 1.25 }} />

      {/* Modal */}
      <BusinessRulesModal
        open={rulesOpen}
        onClose={() => setRulesOpen(false)}
        value={businessRules}
        saving={rulesSaving}
        theme={theme}
        pillButtonSx={pillButtonSx}
        loading={rulesLoading}
        onSave={saveBusinessRules}
      />
      <SmsTestModal open={testOpen} onClose={closeTestModal} config={testConfig} smsApi={smsApi} />
    </SectionCard>
  );
}
